/**
 * nativeTelephonyTransport — the native (Capacitor) implementation of the
 * telephony transport.
 *
 * The Swift/Kotlin `NativeCalling` plugin implementation lives in
 * Lender.Optimus.AdminPanel.Mobile (iOS + Android merged) and is resolved only
 * when getIsNativePlatform() is true — so in the browser/web build this module is
 * never instantiated and remains entirely inert.
 *
 * Plugin method contract (Promise-returning) the native side must implement:
 *   login({ token })                         -> Promise<void>
 *   disconnect()                             -> Promise<void>
 *   dial({ destinationNumber, callerNumber, callerName, clientState })
 *                                            -> Promise<{ callId }>
 *   answer()                                 -> Promise<void>
 *   hangup()                                 -> Promise<void>
 *   mute({ isMuted })                        -> Promise<void>
 *   hold({ isHeld })                         -> Promise<void>
 *   sendDtmf({ digit })                      -> Promise<void>
 *   registerPushToken({ token, platform })   -> Promise<void>
 *   deregisterPushToken()                    -> Promise<void>
 *   getPendingPushCall()
 *     -> Promise<{ callId, callerName, callerNumber, autoAnswer } | null>
 *   connectFromPush({ token, callId, callerName, callerNumber })
 *                                            -> Promise<void>
 *   getVoipToken()                           -> Promise<{ token, platform }>
 *
 * Plugin event contract (emitted from native via notifyListeners / addListener):
 *   'connectionStateChanged' -> { state: 'connecting'|'connected'|'disconnected'|'error' }
 *   'incomingCall'      -> { callId, callerName, callerNumber }
 *   'callAnswered'      -> { callId }
 *   'callEnded'         -> { callId, cause, sipCode }
 *   'callStateChanged'  -> { callId, telnyxCallState }
 */
import { registerPlugin } from '@capacitor/core';
import { telephonyApi } from 'app/services/api';
import _ from 'lodash';

import { logger } from 'utils/logger';
import { reportNativeError, reportNativeEvent } from 'utils/native/nativeTelemetry';

import {
  NATIVE_CALL_TELEMETRY_EVENT_PREFIX,
  NATIVE_CALL_TELEMETRY_SAFE_PROPS,
  NATIVE_CALLING_EVENT,
  NATIVE_CALLING_PLUGIN_NAME,
  TELEPHONY_TRANSPORT_EVENT,
} from './telephonyTransport.constants';
import { createTransportEmitter } from './telephonyTransport.utils';

const toError = error => (_.isError(error) ? error : new Error(String(error)));

// Wraps a plugin command so any rejection is surfaced to App Insights (native shell SDK is disabled),
// then re-thrown so the caller's own error handling is unchanged.
const trackCommand = (command, promise) =>
  promise.catch(error => {
    reportNativeError(toError(error), { command });

    return Promise.reject(error);
  });

/**
 * Builds the native transport and binds native plugin events to the shared
 * emitter. Exposed as a factory (rather than a module-level singleton) so the
 * plugin registration and addListener side-effects run ONLY when the selector
 * resolves the native impl on a native platform — never at web bundle eval time.
 */
const createNativeTelephonyTransport = () => {
  const nativeCallingPlugin = registerPlugin(NATIVE_CALLING_PLUGIN_NAME);
  const emitter = createTransportEmitter();

  // Bridge native plugin events onto the shared emitter so consumers subscribe to
  // the SAME interface regardless of platform. Each event is also forwarded to App
  // Insights (only its non-PII fields — never caller name/number) so the native
  // call lifecycle is observable despite the disabled browser SDK. addListener
  // returns a removable handle; the native plugin is responsible for firing these.
  Object.values(TELEPHONY_TRANSPORT_EVENT).forEach(eventName => {
    nativeCallingPlugin
      .addListener(eventName, payload => {
        // audioSession diagnostics are reported via the buffered PULL path (drainAudioDiagnostics) instead
        // of here: on cold-wake they are emitted natively BEFORE this listener binds, so the live channel
        // drops them. The pull is the authoritative App Insights channel; forwarding here too would
        // double-report the foreground ones (DEV-4332).
        if (eventName !== TELEPHONY_TRANSPORT_EVENT.AUDIO_SESSION) {
          reportNativeEvent(
            `${NATIVE_CALL_TELEMETRY_EVENT_PREFIX}${eventName}`,
            _.pick(payload, NATIVE_CALL_TELEMETRY_SAFE_PROPS[eventName] || []),
          );
        }

        emitter.emit(eventName, payload);
      })
      .catch(error => {
        logger.error(`[TelephonyTransport] Failed to bind native event "${eventName}"`, error);
        reportNativeError(toError(error), { command: `addListener:${eventName}` });
      });
  });

  // DEV-4332: pull + forward the native audio-session diagnostics buffer to App Insights. The native plugin
  // buffers every audio diagnostic (mic permission, category/mode, route, audioUnitStarted, didActivate/
  // didDeactivate) because the cold-wake path emits them during native call setup, before this JS listener
  // binds — the same non-retained notifyListeners race that drops the launch-time VoIP token. Drained on
  // transport mount and after each call (see telephony.actions). getAudioDiagnostics is iOS-only; tolerate
  // its absence on Android and any failure (telemetry must never break a call).
  const drainAudioDiagnostics = async () => {
    if (!_.isFunction(nativeCallingPlugin.getAudioDiagnostics)) {
      return;
    }

    try {
      const { events } = (await nativeCallingPlugin.getAudioDiagnostics()) ?? {};

      (events ?? []).forEach(event =>
        reportNativeEvent(
          `${NATIVE_CALL_TELEMETRY_EVENT_PREFIX}${TELEPHONY_TRANSPORT_EVENT.AUDIO_SESSION}`,
          _.pick(event, NATIVE_CALL_TELEMETRY_SAFE_PROPS[TELEPHONY_TRANSPORT_EVENT.AUDIO_SESSION]),
        ),
      );
    } catch (error) {
      reportNativeError(toError(error), { command: 'getAudioDiagnostics' });
    }
  };

  // DEV-4332 cold-wake register-ready: native emits `registerReady` with the VoIP push's callId the moment
  // it finishes registering the SIP credential for a pending pushed call. The HTTP call is made here (JS
  // owns the authed telephony API client + base URL); native only signals. Best-effort — report success
  // and failure to App Insights (the browser SDK is disabled on native) but never throw.
  const registerReadyEvent = NATIVE_CALLING_EVENT.REGISTER_READY;
  nativeCallingPlugin
    .addListener(registerReadyEvent, async ({ callId } = {}) => {
      reportNativeEvent(`${NATIVE_CALL_TELEMETRY_EVENT_PREFIX}${registerReadyEvent}`, { callId });

      if (!callId) {
        reportNativeError(new Error('registerReady emitted without a callId'), {
          command: registerReadyEvent,
        });

        return;
      }

      await telephonyApi
        .registerInboundReady({ callId })
        .then(() =>
          reportNativeEvent(`${NATIVE_CALL_TELEMETRY_EVENT_PREFIX}${registerReadyEvent}Posted`, {
            callId,
          }),
        )
        .catch(error => reportNativeError(toError(error), { command: registerReadyEvent }));
    })
    .catch(error => {
      logger.error(
        `[TelephonyTransport] Failed to bind native event "${registerReadyEvent}"`,
        error,
      );
      reportNativeError(toError(error), { command: `addListener:${registerReadyEvent}` });
    });

  return {
    login: ({ token } = {}) => trackCommand('login', nativeCallingPlugin.login({ token })),
    // Logout teardown disconnect (DEV-4332 persistent model): drop the persistent TxClient connection on
    // logout ONLY — never on call end. Keeps the VoIP push token + persisted credential (only
    // deregisterPushToken clears those).
    disconnect: () => trackCommand('disconnect', nativeCallingPlugin.disconnect()),
    dial: ({ destinationNumber, callerNumber, callerName, clientState }) =>
      trackCommand(
        'dial',
        nativeCallingPlugin.dial({ destinationNumber, callerNumber, callerName, clientState }),
      ),
    answer: () => trackCommand('answer', nativeCallingPlugin.answer()),
    hangup: () => trackCommand('hangup', nativeCallingPlugin.hangup()),
    mute: isMuted => trackCommand('mute', nativeCallingPlugin.mute({ isMuted })),
    hold: isHeld => trackCommand('hold', nativeCallingPlugin.hold({ isHeld })),
    sendDtmf: digit => trackCommand('sendDtmf', nativeCallingPlugin.sendDtmf({ digit })),
    registerPushToken: ({ token, platform } = {}) =>
      trackCommand('registerPushToken', nativeCallingPlugin.registerPushToken({ token, platform })),
    deregisterPushToken: () =>
      trackCommand('deregisterPushToken', nativeCallingPlugin.deregisterPushToken()),
    // Cold-wake (Android FCM): reads and consumes the call extras from the launch intent when the
    // app was cold-started by tapping the full-screen incoming-call notification. Returns null on a
    // normal launch. Consumed once — subsequent calls return null.
    getPendingPushCall: () =>
      trackCommand('getPendingPushCall', nativeCallingPlugin.getPendingPushCall()),
    // Fast-reconnect path for cold-wake: rejoins the pushed call using a fresh JWT fetched JS-side.
    connectFromPush: ({ token, callId, callerName, callerNumber }) =>
      trackCommand(
        'connectFromPush',
        nativeCallingPlugin.connectFromPush({ token, callId, callerName, callerNumber }),
      ),
    // Pulls the iOS VoIP (PushKit) token directly from the plugin — avoids the event-based gap where
    // notifyListeners fires before the JS listener binds on cold launch.
    getVoipToken: () => trackCommand('getVoipToken', nativeCallingPlugin.getVoipToken()),
    // Drains the native audio-session diagnostics buffer into App Insights (DEV-4332). Best-effort and
    // self-contained (never throws) — see drainAudioDiagnostics above.
    drainAudioDiagnostics,
    on: emitter.on,
    off: emitter.off,
    emit: emitter.emit,
  };
};

export default createNativeTelephonyTransport;
